import { PreconditionFailedError } from "./errors";

/** ISO-string shape accepted and returned by domain model boundaries. */
export interface TimeRangeProps {
  start: string;
  end: string;
}

/**
 * A closed pair of instants with a strictly positive duration.
 *
 * Dates are copied on the way in and out so the range remains immutable even
 * though JavaScript's Date object is mutable. ISO conversion belongs at
 * persistence and transport boundaries, not in the domain value itself.
 */
export class TimeRange {
  readonly #start: number;
  readonly #end: number;

  constructor(start: Date, end: Date) {
    const startTime = start.getTime();
    const endTime = end.getTime();
    if (!Number.isFinite(startTime) || !Number.isFinite(endTime)) {
      throw new PreconditionFailedError("Time range requires valid start and end instants");
    }
    if (endTime <= startTime) {
      throw new PreconditionFailedError("End must be after start");
    }
    this.#start = startTime;
    this.#end = endTime;
  }

  get start(): Date {
    return new Date(this.#start);
  }

  get end(): Date {
    return new Date(this.#end);
  }

  contains(other: TimeRange): boolean {
    return this.#start <= other.#start && other.#end <= this.#end;
  }

  overlaps(other: TimeRange): boolean {
    return this.#start < other.#end && other.#start < this.#end;
  }
}
